/**
 * useInitialSyncReady — hook that tracks sync readiness during initial data download.
 *
 * Subscribes to `db.cloud.syncState` and determines when the initial
 * data download is complete. Used by DatabaseProvider to control
 * InitialSyncLoader dismissal.
 *
 * Implements:
 * - Minimum 1.5s display (prevent jarring flash for instant syncs)
 * - Maximum 5min timeout (large accounts can take minutes for initial sync)
 * - Phase-aware progress tracking for UI messaging
 * - "error"/"offline" only dismiss loader if data exists in IDB
 *   (prevents showing empty app when sync fails on fresh login)
 */

import { useEffect, useRef, useState } from "react"
import { db } from "@/db/schema"

/** Sync phase type from dexie-cloud-addon (not exported from package root) */
type SyncStatePhase = "initial" | "not-in-sync" | "pushing" | "pulling" | "in-sync" | "error" | "offline"

export type LoaderPhase = SyncStatePhase | "timeout"

const MIN_DISPLAY_MS = 1500
const MAX_DISPLAY_MS = 300_000

export interface FirstLoginReadyState {
  /** True when sync is done AND minimum display time has passed */
  isReady: boolean
  /** Current sync phase for messaging */
  phase: LoaderPhase
  /** True when sync itself is done (regardless of min display) */
  isSyncDone: boolean
}

/**
 * Track first-login readiness based on actual Dexie Cloud sync state.
 *
 * @param active - Whether this hook should be actively tracking
 *                 (false = returns isReady: true immediately)
 */
export function useInitialSyncReady(active: boolean): FirstLoginReadyState {
  const [phase, setPhase] = useState<LoaderPhase>("initial")
  const [isSyncDone, setIsSyncDone] = useState(false)
  const [minTimePassed, setMinTimePassed] = useState(false)
  const subscribed = useRef(false)

  useEffect(() => {
    if (!active || subscribed.current) return
    subscribed.current = true

    // Minimum display timer
    const minTimer = setTimeout(() => {
      setMinTimePassed(true)
    }, MIN_DISPLAY_MS)

    // Maximum display timer (fallback for offline/stuck scenarios)
    const maxTimer = setTimeout(() => {
      setPhase("timeout")
      setIsSyncDone(true)
    }, MAX_DISPLAY_MS)

    // Subscribe to sync state
    const sub = db.cloud.syncState.subscribe((state) => {
      if (!state) return
      setPhase(state.phase as SyncStatePhase)

      if (state.phase === "in-sync") {
        // Sync completed successfully — always dismiss
        setIsSyncDone(true)
        clearTimeout(maxTimer)
      } else if (state.phase === "error" || state.phase === "offline") {
        // Sync failed — only dismiss if there's data to show.
        // Without data, the user would see an empty app with no indication
        // that sync is still retrying. Keep the loader visible instead.
        void db.totodo.count().then((count) => {
          if (count > 0) {
            setIsSyncDone(true)
            clearTimeout(maxTimer)
          }
          // If count === 0: keep loader visible, sync will retry
          // (5s → 10s → 30s → 60s for 5xx, 20s → 40s → 5min → 15min
          // for other errors). Loader shows "Starting in offline mode".
        })
      }
    })

    return () => {
      clearTimeout(minTimer)
      clearTimeout(maxTimer)
      sub.unsubscribe()
    }
  }, [active])

  return {
    isReady: !active || (isSyncDone && minTimePassed),
    phase,
    isSyncDone,
  }
}
